/**
 * Pixa Asistan — Bulgular.
 *
 * Uyarı motorunun (backend app/pixa_anomalies.py) ürettiği grupları "ne oldu → ne
 * demek → ne yapmalısınız → hangi kayıtlar" düzeninde gösterir. Liste ucu grup başına
 * birkaç örnek döner; kart açılınca /anomalies/{id} ile etkilenen kayıtların TAMAMI
 * (SKU tablosu) çekilir. Fiyat bulguları ayrıca kanallar arası karşılaştırılıp
 * "Pixa ana fiyatı güncel değil" gibi somut içgörüler çıkarılır.
 *
 * Aksiyon kuralları:
 * - Pazaryerine yazan her şey (gönder, yeniden çalıştır) ONAYLA modalından geçer.
 * - Buton metni ilgili platformu söyler ("Boyner'e Gönder"), genel "pazaryerine" değil.
 * - "Bağlantıyı Test Et", Pixa'nın güvenilmez /integrations/check'i yerine veriye
 *   dayalı /integrations/health karnesini gösterir.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  API, T, MONO, Button, Card, ConfirmDangerModal, Icon, Loader, Toast, axios, exportCsv,
  fmtDateTime, fmtN, inputStyle, money, timeAgo, tint, useIntegrations, useLang, upperUi,
} from './shared';
import { addTranslations } from '../i18n';
import { useViewport } from '../poyais-ui/hooks';
import { goToFinding, findingTarget } from './focus';
import { MarketMark } from './marketplaces';

addTranslations({
  'Mağaza Sağlığı': 'Store Health', 'Hemen İlgilenin': 'Act Now', 'Satış Kaybı Riski': 'Revenue at Risk',
  'Gözden Geçirin': 'Review', 'Bu ne demek?': 'What does this mean?', 'Ne yapmalısınız?': 'What should you do?',
  'Etkilenen kayıtlar': 'Affected records', 'Tümünü Göster': 'Show all', 'Yeniden Gönder': 'Resend',
  'AI Çözüm Önerisi': 'AI Fix Suggestion', 'AI düşünüyor…': 'AI is thinking…', 'Kanal Durumu': 'Channel Status',
  'Bağlantıyı Test Et': 'Test Connection', 'Test ediliyor…': 'Testing…', 'Sağlıklı': 'Healthy', 'Dikkat': 'Attention',
  'Sorunlu': 'Problem', 'Kapalı': 'Off', 'açık bulgu': 'open findings', 'Sorun yok': 'No issues',
  'Öncelik Sırası': 'Priority Order', 'Akıllı İçgörüler': 'Smart Insights', 'Şimdi Tara': 'Scan Now',
  'Taranıyor…': 'Scanning…', 'Son tarama': 'Last scan', 'Gördüm': 'Dismiss', 'görüldü': 'seen',
  'Her şey yolunda': 'All good', 'Açık bulgu yok.': 'No open findings.', 'kayıt': 'records',
  'Pazaryeri Fiyatı': 'Marketplace Price', 'Pixa Fiyatı': 'Pixa Price', 'Fark': 'Diff', 'Kampanya': 'Campaign',
  'Neden': 'Reason', 'Stok': 'Stock', 'Maliyet': 'Cost', 'Marj': 'Margin', 'Pazaryeri ID': 'Marketplace ID',
  'Zaman': 'Time', 'Ürün': 'Product', 'Ara…': 'Search…', 'CSV İndir': 'Download CSV',
  'Bu kanalları fiyat kuralından hariç tut': 'Exclude these channels from the price rule',
  'Hariç tutuldu. Sonraki taramada bu kanallar için fiyat uyuşmazlığı üretilmez.': 'Excluded. The next scan won’t flag price mismatches for these channels.',
  'Görev Pixa\'ya iletildi.': 'Task sent to Pixa.', 'Tarama tamamlandı.': 'Scan finished.',
  'AI ile Haftalık Plan': 'Weekly Plan with AI', 'Listeleri Gör': 'View Listings', 'Soruları Cevapla': 'Answer Questions',
  'Kargoları Gör': 'View Shipments', 'Stoğa Git': 'Go to Stock', 'Siparişleri Gör': 'View Orders',
  'Görevi Yeniden Çalıştır': 'Rerun Task', 'Fiyatları Karşılaştır': 'Compare Prices',
});

const SEV_COLOR: Record<string, string> = { high: T.red, medium: T.amber, low: T.blue, info: T.textDim };
const SEV_TEXT: Record<string, string> = { high: T.redText, medium: T.amberText, low: T.blueText, info: T.textDim };
const SEV_LABEL: Record<string, string> = { high: 'Kritik', medium: 'Orta', low: 'Düşük', info: 'Bilgi' };

// ── Platform adı yardımcıları ────────────────────────────────────────────────

/** Türkçe yönelme eki: "Boyner'e", "Trendyol'a", "Hepsiburada'ya". */
const DATIVE: Record<string, string> = {
  trendyol: "Trendyol'a", hepsiburada: "Hepsiburada'ya", boyner: "Boyner'e", shopify: "Shopify'a",
  ticimax: "Ticimax'e", amazon: "Amazon'a", n11: "N11'e", ciceksepeti: "Çiçeksepeti'ne", pazarama: "Pazarama'ya",
  pttavm: "PttAVM'ye", idefix: "Idefix'e", ikas: "İkas'a", tsoft: "T-Soft'a", akinon: "Akinon'a",
};
export function dative(name: string) {
  const k = (name || '').toLowerCase().replace(/[\s-]/g, '');
  if (DATIVE[k]) return DATIVE[k];
  const vowels = (name.match(/[aeıioöuüAEIİOÖUÜ]/g) || []);
  const last = (vowels[vowels.length - 1] || 'a').toLowerCase();
  const suf = 'aıou'.includes(last) ? 'a' : 'e';
  return /[aeıioöuü]$/i.test(name) ? `${name}'y${suf}` : `${name}'${suf}`;
}
/** Bulunma eki: "Boyner'de", "Trendyol'da". */
function locative(name: string) {
  const d = dative(name);
  return d.replace(/'y?a$/, "'da").replace(/'y?e$/, "'de").replace(/'n?e$/, "'de").replace(/'n?a$/, "'da");
}

/** Bulgu kapsamından (scope) Pixa entegrasyon adını bulur. "Hepsiburada eşleşmeyen
 *  ürünler" → Hepsiburada, "Nebim sipariş aktarımı" → NebimV3New, "Boyner · updatePrice" → Boyner. */
export function resolvePlatform(scope: string, integrations: any[]): string {
  const s = (scope || '').split('·')[0].trim();
  if (!s) return '';
  const low = s.toLocaleLowerCase('tr');
  const exact = integrations.find((i: any) => String(i.resourceName).toLocaleLowerCase('tr') === low);
  if (exact) return exact.resourceName;
  const contained = integrations.find((i: any) => low.includes(String(i.resourceName).toLocaleLowerCase('tr')));
  if (contained) return contained.resourceName;
  const first = low.split(/\s+/)[0];
  const prefix = integrations.find((i: any) => String(i.resourceName).toLocaleLowerCase('tr').startsWith(first));
  return prefix ? prefix.resourceName : s;
}

// ── Bulgu türü sözlüğü: ne demek, ne yapmalı, hangi sütunlar ────────────────

type Rec = Record<string, any>;
type KindInfo = {
  title: string;
  meaning: (p: string, g: any) => string;
  todo: (p: string, g: any, recs: Rec[]) => string[];
  cols: ('sku' | 'name' | 'market' | 'pixa' | 'diff' | 'camp' | 'reason' | 'stock' | 'cost' | 'margin' | 'mpId' | 'time')[];
};

const stocked = (recs: Rec[]) => recs.filter(r => Number(r.pixaStock) > 0).length;

const KINDS: Record<string, KindInfo> = {
  product_send_failed: {
    title: 'Pazaryerine Gönderilemeyen Ürün',
    meaning: p => `Pixa bu ürünleri ${dative(p)} göndermeyi denedi, pazaryeri kabul etmedi. Bu ürünler ${locative(p)} satışa çıkmıyor ya da güncellenmiyor.`,
    todo: p => [
      'Pixa\'da ürün kartını açın: liste fiyatı ve satış fiyatı dolu mu kontrol edin.',
      `Ürün kartında "${dative(p)} gönder" seçeneğinin açık olduğundan emin olun.`,
      `Düzelttikten sonra "${dative(p)} Yeniden Gönder" ile tekrar deneyin.`,
    ],
    cols: ['sku', 'name', 'reason', 'pixa', 'time'],
  },
  listing_closed: {
    title: 'Kapalı Listing',
    meaning: p => `Bu ürünler ${locative(p)} satışa kapalı: müşteri ürünü göremiyor ya da satın alamıyor.`,
    todo: (p, g, recs) => {
      const n = stocked(recs);
      return [
        n > 0 ? `${n} ürünün Pixa'da stoğu var — bunlar doğrudan satış kaybı. Önce bunları açın.` : 'Kapalı ürünlerin hiçbirinde Pixa stoğu yok; stok gelince açılmaları yeterli.',
        `Satılması gerekenleri seçip "${dative(p)} Gönder" ile yeniden yayına alın.`,
        'Bilerek kapattıysanız "Gördüm" deyin; kart zilde tekrar çıkmaz.',
      ];
    },
    cols: ['sku', 'name', 'stock', 'mpId'],
  },
  listing_inactive: {
    title: 'Pasif Listing',
    meaning: p => `Listing Pixa'da pasif işaretli: ${dative(p)} stok ve fiyat güncellemesi gitmez, eski bilgilerle satışta kalabilir.`,
    todo: p => [
      'Satılmaya devam edecek ürünleri Pixa\'da aktifleştirin.',
      `Ardından "${dative(p)} Gönder" ile güncel stok/fiyatı iletin.`,
    ],
    cols: ['sku', 'name', 'stock'],
  },
  listing_locked: {
    title: 'Kilitli Listing',
    meaning: p => `${p} bu listingleri kilitlemiş. Kilit kalkana kadar Pixa'dan gönderilen fiyat/stok güncellemeleri uygulanmaz.`,
    todo: p => [
      `${p} satıcı panelinde ilgili ürünlerin kilit nedenine bakın (kampanya, moderasyon, fiyat kontrolü).`,
      'Kilit kalkınca Pixa bir sonraki gönderimde ürünleri otomatik günceller.',
    ],
    cols: ['sku', 'name', 'mpId', 'stock'],
  },
  listing_disconnected: {
    title: 'Bağlantısı Kopmuş Listing',
    meaning: p => `Pixa ile ${p} ürünü arasındaki bağ kopmuş; siparişler ve stok bu ürünler için doğru akmaz.`,
    todo: p => [`"${locative(p)} Eşleştir" görevini çalıştırın.`, 'Düzelmeyenlerde barkod/SKU\'nun iki tarafta aynı olduğunu kontrol edin.'],
    cols: ['sku', 'name', 'mpId'],
  },
  listing_not_matched: {
    title: 'Pazaryerinde Eşleşmeyen Ürün',
    meaning: p => `${p} ürünü Pixa kataloğundaki hiçbir ürünle eşleşmemiş.`,
    todo: p => [`"${locative(p)} Eşleştir" görevini çalıştırın.`, 'Eşleşmeyenlerde barkod veya SKU farkını düzeltin.'],
    cols: ['sku', 'name', 'mpId'],
  },
  not_listed: {
    title: 'Pazaryerinde Listelenmemiş Ürün',
    meaning: p => `Stoğu olan aktif ürünler ${locative(p)} hiç listelenmemiş — satış fırsatı kaçıyor.`,
    todo: p => [`Listelenmesini istediklerinizi "${dative(p)} Gönder" ile yayına alın.`, 'Kanalda satmak istemediğiniz ürünler varsa Pixa\'da o kanala gönderimi kapatın.'],
    cols: ['sku', 'name', 'stock', 'pixa'],
  },
  price_deviation: {
    title: 'Fiyat Uyuşmazlığı',
    meaning: p => `${locative(p)} satış fiyatı, Pixa'daki ana satış fiyatından belirgin şekilde farklı.`,
    todo: () => [
      'Kanal bazlı bilinçli fiyatlama yapıyorsanız (ör. pazaryerine özel indirim) bu kanalı Kurallar\'dan hariç tutun.',
      'Değilse farkı en büyük olanlardan başlayarak Pixa fiyatını ya da kanal fiyatını düzeltin.',
      'Aşağıdaki "Akıllı İçgörüler" bölümünde Pixa ana fiyatı güncel olmayan ürünler ayrıca listelenir.',
    ],
    cols: ['sku', 'name', 'market', 'pixa', 'diff', 'camp'],
  },
  price_below_cost: {
    title: 'Maliyetin Altında Fiyat',
    meaning: p => `Bu ürünler ${locative(p)} maliyetinin altında satılıyor; her satış zarar yazıyor.`,
    todo: () => ['Kampanya fiyatlarını ve kanal indirimlerini kontrol edin.', 'Maliyet bilgisi hatalıysa Pixa\'da ürün maliyetini düzeltin.'],
    cols: ['sku', 'name', 'market', 'cost', 'margin'],
  },
  low_stock: {
    title: 'Kritik Stok',
    meaning: (p, g) => /tükendi/.test(g.detail || '') ? 'Bu ürünlerin stoğu tükenmiş; hiçbir kanalda satılamıyorlar.' : 'Bu ürünlerin stoğu kritik eşiğin altında; yakında satılamaz hale gelecekler.',
    todo: () => ['Tedarik/üretim siparişi verin.', 'Bu ürünler reklamlarda öne çıkıyorsa bütçeyi stoğu olan ürünlere kaydırın.'],
    cols: ['sku', 'name', 'stock'],
  },
  pending_questions: {
    title: 'Cevaplanmamış Müşteri Sorusu',
    meaning: () => 'Müşteriler ürün hakkında soru sordu ve cevap bekliyor. Süre dolarsa pazaryeri soruyu kapatır.',
    todo: () => ['Müşteri Soruları sekmesinden AI taslağıyla hızlıca cevaplayın.'],
    cols: ['name', 'time'],
  },
  problematic_shipment: {
    title: 'Sorunlu Kargo',
    meaning: () => 'Bu siparişlerin kargo sürecinde sorun var (gecikme, iade, teslim edilemedi).',
    todo: () => ['Kargo sayfasında takip numarasıyla durumu kontrol edin, müşteriyi bilgilendirin.'],
    cols: ['name', 'market'],
  },
  transfer_failed: {
    title: 'Pazaryeri Aktarımı Başarısız',
    meaning: p => `${dative(p)} gönderilen toplu görevde başarısız kayıtlar var.`,
    todo: () => ['Hata nedenini aşağıdaki listeden okuyun, ürün verisini düzeltin.', 'Ardından "Görevi Yeniden Çalıştır".'],
    cols: ['name', 'reason'],
  },
  integration_down: {
    title: 'Entegrasyon Pasif',
    meaning: p => `${p} entegrasyonu Pixa'da pasif: bu kanala sipariş, stok ve fiyat akışı durmuş durumda.`,
    todo: p => [`Bilerek kapatmadıysanız Pixa panelinden ${p} entegrasyonunu açın.`, '"Bağlantıyı Test Et" ile ardından kontrol edin.'],
    cols: [],
  },
  integration_error: {
    title: 'Entegrasyon Hatası',
    meaning: (p, g) => /eşleşmeyen/i.test(g.scope || '')
      ? `Pixa, ${locative(p)} satılan bazı ürünleri kendi kataloğundaki bir ürünle eşleştiremedi (barkod/SKU uyuşmuyor). Bu ürünlerin siparişleri ve stokları Pixa'ya doğru akmaz.`
      : `${p} ile veri alışverişinde tekrar eden bir hata var: "${g.scope}".`,
    todo: (p, g) => /eşleşmeyen/i.test(g.scope || '')
      ? [`"${locative(p)} Eşleştir" görevini çalıştırın.`, 'Sorun sürerse Pixa panelindeki Eşleşmeyen Ürünler raporundan barkodları düzeltin (bu liste Pixa\'nın API\'sinde paylaşılmıyor).']
      : /sipariş/i.test(g.scope || '')
        ? ['Siparişler sayfasında ERP durumu hatalı olanları bulun ve ERP\'ye yeniden gönderin.']
        : ['"Bağlantıyı Test Et" ile kanalın durumunu kontrol edin.'],
    cols: ['time'],
  },
};

export const kindInfo = (k: string): KindInfo => KINDS[k] || { title: k, meaning: () => '', todo: () => [], cols: ['sku', 'name'] };

// ── Aksiyonlar ───────────────────────────────────────────────────────────────

type Action = { id: string; label: string; push?: boolean; nav?: boolean; primary?: boolean };
function actionsFor(g: any, p: string): Action[] {
  const eslestir = /eşleşmeyen/i.test(g.scope || '');
  switch (g.kind) {
    case 'product_send_failed':
      return [{ id: 'send', label: `${dative(p)} Yeniden Gönder`, push: true, primary: true }, { id: 'nav', label: 'Listeleri Gör', nav: true }];
    case 'listing_closed': case 'listing_inactive': case 'not_listed':
      return [{ id: 'send', label: `${dative(p)} Gönder`, push: true, primary: true }, { id: 'nav', label: 'Listeleri Gör', nav: true }];
    case 'listing_not_matched': case 'listing_disconnected':
      return [{ id: 'match', label: `${locative(p)} Eşleştir`, primary: true }, { id: 'nav', label: 'Listeleri Gör', nav: true }];
    case 'listing_locked':
      return [{ id: 'nav', label: 'Listeleri Gör', nav: true }];
    case 'price_deviation': case 'price_below_cost':
      return [{ id: 'nav', label: 'Fiyatları Karşılaştır', nav: true, primary: true }];
    case 'low_stock': return [{ id: 'nav', label: 'Stoğa Git', nav: true, primary: true }];
    case 'pending_questions': return [{ id: 'questions', label: 'Soruları Cevapla', primary: true }];
    case 'problematic_shipment': return [{ id: 'nav', label: 'Kargoları Gör', nav: true, primary: true }];
    case 'transfer_failed': return [{ id: 'retry', label: 'Görevi Yeniden Çalıştır', push: true, primary: true }];
    case 'integration_down': return [{ id: 'health', label: 'Bağlantıyı Test Et', primary: true }];
    case 'integration_error':
      return eslestir
        ? [{ id: 'match', label: `${locative(p)} Eşleştir`, primary: true }, { id: 'health', label: 'Bağlantıyı Test Et' }]
        : /sipariş/i.test(g.scope || '')
          ? [{ id: 'nav-orders', label: 'Siparişleri Gör', primary: true }, { id: 'health', label: 'Bağlantıyı Test Et' }]
          : [{ id: 'health', label: 'Bağlantıyı Test Et', primary: true }];
    default: return [];
  }
}

// ── Sağlık karnesi (Bağlantıyı Test Et) ─────────────────────────────────────

const VERDICT: Record<string, { label: string; color: string; text: string }> = {
  ok: { label: 'Sağlıklı', color: T.green, text: T.greenText },
  warn: { label: 'Dikkat', color: T.amber, text: T.amberText },
  bad: { label: 'Sorunlu', color: T.red, text: T.redText },
  down: { label: 'Kapalı', color: T.textDim, text: T.textDim },
};
const CHECK_COLOR: Record<string, string> = { ok: T.green, warn: T.amber, bad: T.red, info: T.blue, unknown: T.textDim };

export function HealthReport({ clientId, platform, onClose, compact }: { clientId: number; platform: string; onClose?: () => void; compact?: boolean }) {
  const { t } = useLang();
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = () => {
    setData(null); setErr(null);
    axios.get(`${API}/pixa/${clientId}/integrations/health`, { params: { platform } })
      .then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || t('Hata')));
  };
  useEffect(run, [clientId, platform]); // eslint-disable-line react-hooks/exhaustive-deps
  const v = data ? VERDICT[data.verdict] || VERDICT.warn : null;
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: '12px 14px', background: 'var(--py-subtle-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: data ? 10 : 0 }}>
        {!compact && <MarketMark name={platform} size={18} />}
        {!compact && <b style={{ fontSize: 12.5, color: T.text }}>{platform}</b>}
        {v && <span style={{ fontSize: 10.5, fontWeight: 800, color: v.text, background: tint(v.color, 14), borderRadius: 999, padding: '2px 9px' }}>{t(v.label)}</span>}
        {!data && !err && <span style={{ fontSize: 11.5, color: T.textDim }}>{t('Test ediliyor…')}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {data && <button onClick={run} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textDim, fontSize: 11, fontFamily: 'inherit' }}>↻</button>}
          {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textDim, fontSize: 13, fontFamily: 'inherit' }}>✕</button>}
        </span>
      </div>
      {err && <div style={{ fontSize: 11.5, color: T.redText, marginTop: 6 }}>{err}</div>}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.checks.map((c: any) => (
            <div key={c.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: T.text, lineHeight: 1.45 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: CHECK_COLOR[c.status] || T.textDim, marginTop: 5, flexShrink: 0 }} />
              <span>{c.label}{c.hint && <span style={{ display: 'block', fontSize: 11, color: T.textDim }}>{c.hint}</span>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Kayıt tablosu (kart içi + Listeler sayfasında bulgu modu) ────────────────

const PER_PAGE = 50;

export function FindingRecords({ kind, records, platform }: { kind: string; records: Rec[]; platform?: string }) {
  const { t } = useLang();
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const info = kindInfo(kind);
  const cols = info.cols;
  const sorted = useMemo(() => {
    const rs = [...records];
    if (cols.includes('diff')) rs.sort((a, b) => Math.abs(Number(b.diffPct || 0)) - Math.abs(Number(a.diffPct || 0)));
    else if (cols.includes('stock') && kind !== 'low_stock') rs.sort((a, b) => Number(b.pixaStock || 0) - Number(a.pixaStock || 0));
    return rs;
  }, [records, cols, kind]);
  const needle = q.trim().toLocaleLowerCase('tr');
  const rows = needle ? sorted.filter(r => [r.sku, r.name, r.value, r.mpId].some(x => String(x || '').toLocaleLowerCase('tr').includes(needle))) : sorted;
  if (!cols.length || !records.length) return null;

  const H: Record<string, string> = { sku: 'SKU', name: 'Ürün', market: 'Pazaryeri Fiyatı', pixa: 'Pixa Fiyatı', diff: 'Fark', camp: 'Kampanya',
    reason: 'Neden', stock: 'Stok', cost: 'Maliyet', margin: 'Marj', mpId: 'Pazaryeri ID', time: 'Zaman' };
  const cell = (r: Rec, c: string): React.ReactNode => {
    switch (c) {
      case 'sku': return <span style={{ fontFamily: MONO, fontSize: 11.5, color: T.text }}>{r.sku || '—'}</span>;
      case 'name': return <span title={r.name} style={{ display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.name || '—'}</span>;
      case 'market': return r.effectivePrice ? money(r.effectivePrice) : '—';
      case 'pixa': return r.pixaPrice ? money(r.pixaPrice) : '—';
      case 'diff': {
        const d = Number(r.diffPct);
        if (!isFinite(d)) return '—';
        return <b style={{ color: Math.abs(d) >= 50 ? T.redText : T.amberText }}>{d > 0 ? '+' : ''}%{Math.abs(d).toLocaleString('tr-TR')}</b>;
      }
      case 'camp': return r.campaignSalePrice ? money(r.campaignSalePrice) : '—';
      case 'reason': return <span style={{ color: T.textMid }}>{r.value || '—'}</span>;
      case 'stock': return <b style={{ color: Number(r.pixaStock ?? r.value) > 0 ? T.text : T.redText }}>{fmtN(r.pixaStock ?? r.value)}</b>;
      case 'cost': return r.cost ? money(r.cost) : '—';
      case 'margin': return r.marginPct !== undefined ? <b style={{ color: T.redText }}>%{Number(r.marginPct).toLocaleString('tr-TR')}</b> : '—';
      case 'mpId': return r.mpLink ? <a href={r.mpLink} target="_blank" rel="noreferrer" style={{ fontFamily: MONO, fontSize: 11, color: T.accent }}>{r.mpId || '↗'}</a>
        : <span style={{ fontFamily: MONO, fontSize: 11, color: T.textDim }}>{r.mpId || '—'}</span>;
      case 'time': return <span style={{ color: T.textDim }}>{fmtDateTime(r.at || r.value)}</span>;
      default: return '—';
    }
  };
  const csv = () => exportCsv(`pixa-${kind}${platform ? '-' + platform : ''}.csv`, rows.map(r => ({
    sku: r.sku, urun: r.name, pazaryeri_fiyati: r.effectivePrice, pixa_fiyati: r.pixaPrice, fark_yuzde: r.diffPct,
    kampanya: r.campaignSalePrice, stok: r.pixaStock, neden: r.value, pazaryeri_id: r.mpId,
  })), [
    { key: 'sku', label: 'SKU' }, { key: 'urun', label: 'Ürün' }, { key: 'pazaryeri_fiyati', label: 'Pazaryeri Fiyatı' },
    { key: 'pixa_fiyati', label: 'Pixa Fiyatı' }, { key: 'fark_yuzde', label: 'Fark %' }, { key: 'kampanya', label: 'Kampanya' },
    { key: 'stok', label: 'Stok' }, { key: 'neden', label: 'Neden' }, { key: 'pazaryeri_id', label: 'Pazaryeri ID' },
  ]);
  const shown = all ? rows : rows.slice(0, PER_PAGE);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' as const }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.06em' }}>{upperUi(t('Etkilenen kayıtlar'))} · {fmtN(rows.length)}</span>
        {records.length > 8 && <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('Ara…')} style={{ ...inputStyle, marginLeft: 'auto', width: 180, padding: '5px 9px' }} />}
        <Button variant="ghost" color={T.accent} onClick={csv} style={{ padding: '4px 10px', fontSize: 11, marginLeft: records.length > 8 ? 0 : 'auto' }}>{t('CSV İndir')}</Button>
      </div>
      <div style={{ overflowX: 'auto' as const, border: `1px solid ${T.border}`, borderRadius: 10, background: T.surface }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 }}>
          <thead><tr>{cols.map(c => (
            <th key={c} style={{ textAlign: ['name', 'sku', 'reason'].includes(c) ? 'left' as const : 'right' as const, padding: '8px 10px', fontSize: 10.5, fontWeight: 800,
              color: T.textDim, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' as const, background: 'var(--py-subtle-bg)' }}>{t(H[c])}</th>))}</tr></thead>
          <tbody>{shown.map((r, i) => (
            <tr key={i} style={{ borderBottom: i < shown.length - 1 ? `1px solid ${T.border}` : 'none' }}>
              {cols.map(c => <td key={c} style={{ padding: '7px 10px', textAlign: ['name', 'sku', 'reason'].includes(c) ? 'left' as const : 'right' as const, color: T.text, whiteSpace: c === 'reason' ? 'normal' as const : 'nowrap' as const }}>{cell(r, c)}</td>)}
            </tr>))}</tbody>
        </table>
      </div>
      {!all && rows.length > PER_PAGE && (
        <div style={{ textAlign: 'center' as const, marginTop: 8 }}>
          <Button variant="ghost" color={T.accent} onClick={() => setAll(true)} style={{ padding: '5px 12px', fontSize: 11 }}>{t('Tümünü Göster')} ({fmtN(rows.length)})</Button>
        </div>
      )}
    </div>
  );
}

// ── Kanallar arası fiyat içgörüsü ───────────────────────────────────────────

type PriceInsight = { products: { productId: any; sku: string; name: string; pixa: number; prices: Record<string, number> }[]; platforms: string[] };

/** Aynı ürün ≥2 pazaryerinde aynı (±%2) fiyattan satılıyor ama Pixa ana fiyatı farklıysa,
 *  sorun büyük ihtimalle kanallarda değil Pixa'daki ana fiyatta (ya da bilinçli kanal kuralı). */
function priceInsight(groups: any[], details: Record<number, any>): PriceInsight | null {
  const by: Record<string, { sku: string; name: string; pixa: number; prices: Record<string, number> }> = {};
  for (const g of groups.filter(x => x.kind === 'price_deviation')) {
    const recs: Rec[] = details[g.id]?.sample || g.sample || [];
    for (const r of recs) {
      if (!r.productId || !r.effectivePrice || !r.pixaPrice) continue;
      const k = String(r.productId);
      by[k] = by[k] || { sku: r.sku, name: r.name, pixa: Number(r.pixaPrice), prices: {} };
      by[k].prices[r.platform || g.scope] = Number(r.effectivePrice);
    }
  }
  const products = Object.entries(by).filter(([, v]) => {
    const ps = Object.values(v.prices);
    if (ps.length < 2) return false;
    const mn = Math.min(...ps), mx = Math.max(...ps);
    return (mx - mn) / mx <= 0.02;
  }).map(([productId, v]) => ({ productId, ...v }));
  if (!products.length) return null;
  const platforms = Array.from(new Set(products.flatMap(p => Object.keys(p.prices))));
  return { products, platforms };
}

// ── Ana bileşen ─────────────────────────────────────────────────────────────

export function Findings({ clientId, onGoQuestions }: { clientId: number; onGoQuestions: () => void }) {
  const { t } = useLang();
  const { isMobile } = useViewport();
  const integrations = useIntegrations(clientId, false);
  const [data, setData] = useState<any>(null);
  const [details, setDetails] = useState<Record<number, any>>({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [note, setNote] = useState<{ msg: string; tone: 'ok' | 'err' }>({ msg: '', tone: 'ok' });
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [health, setHealth] = useState<string | null>(null);
  const [danger, setDanger] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [plan, setPlan] = useState<{ busy: boolean; text: string | null; err: string | null }>({ busy: false, text: null, err: null });

  const load = () => {
    setLoading(true);
    axios.get(`${API}/pixa/${clientId}/anomalies`, { params: { limit: 100 } })
      .then(r => {
        setData(r.data);
        // Kayıt listeleri küçük (DB'den); içgörüler için hepsi baştan çekiliyor.
        const rows = (r.data?.rows || []).filter((g: any) => (g.sample_total || 0) > (g.sample || []).length);
        Promise.all(rows.map((g: any) => axios.get(`${API}/pixa/${clientId}/anomalies/${g.id}`).then(x => [g.id, x.data]).catch(() => null)))
          .then(list => setDetails(Object.fromEntries(list.filter(Boolean) as any)));
      })
      .catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(load, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (msg: string, tone: 'ok' | 'err' = 'ok') => { setNote({ msg, tone }); setTimeout(() => setNote({ msg: '', tone: 'ok' }), 6000); };
  const recordsOf = (g: any): Rec[] => details[g.id]?.sample || g.sample || [];
  const toggle = (id: number) => setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const scan = async () => {
    setScanning(true);
    try { await axios.post(`${API}/pixa/${clientId}/anomalies/scan`); flash(t('Tarama tamamlandı.')); load(); }
    catch (e: any) { flash(e?.response?.data?.detail || t('Hata'), 'err'); }
    finally { setScanning(false); }
  };
  const ack = async (g: any) => {
    try { await axios.post(`${API}/pixa/${clientId}/anomalies/${g.id}/ack`); load(); }
    catch (e: any) { flash(e?.response?.data?.detail || t('Hata'), 'err'); }
  };

  const runAction = async (g: any, a: Action, p: string) => {
    if (a.id === 'questions') { onGoQuestions(); return; }
    if (a.id === 'nav') {
      const target = findingTarget(g.kind, p);
      if (target) {
        try { sessionStorage.setItem('poyais_pixa_focus', JSON.stringify({ ...target, groupId: g.id, platform: p })); } catch { }
        window.dispatchEvent(new CustomEvent('poyais-nav', { detail: target.nav }));
      } else goToFinding(g.kind, g.scope);
      return;
    }
    if (a.id === 'nav-orders') { window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'pixa-orders' })); return; }
    if (a.id === 'health') { setHealth(p); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (a.push) { setErr(null); setDanger({ group: g, action: a, platform: p }); return; }
    if (a.id === 'match') {
      try {
        await axios.post(`${API}/pixa/${clientId}/integrations/market-task`, { platform: p, action: 'matchProducts' });
        flash(`${t('Görev Pixa\'ya iletildi.')} (${locative(p)} eşleştirme)`);
      } catch (e: any) { flash(e?.response?.data?.detail || t('Hata'), 'err'); }
    }
  };

  const confirmAction = async () => {
    if (!danger) return;
    const { group: g, action: a, platform: p } = danger;
    setBusy(true); setErr(null);
    try {
      if (a.id === 'retry') {
        const taskType = String(g.scope || '').split('·')[1]?.trim();
        await axios.post(`${API}/pixa/${clientId}/integrations/market-task`, { platform: p, action: taskType, confirm: 'ONAYLA' });
      } else if (a.id === 'send') {
        const productIds = Array.from(new Set(recordsOf(g).map(s => s.productId).filter(Boolean)));
        await axios.post(`${API}/pixa/${clientId}/integrations/market-task`, {
          platform: p, action: 'sendProducts', confirm: 'ONAYLA', ...(productIds.length ? { productIds } : {}),
        });
      }
      setDanger(null); flash(`${t('Görev Pixa\'ya iletildi.')} (${a.label})`);
    } catch (e: any) { setErr(e?.response?.data?.detail || t('Hata')); }
    finally { setBusy(false); }
  };

  const excludeChannels = async (platforms: string[]) => {
    try {
      const r = await axios.get(`${API}/pixa/${clientId}/anomalies/rules`);
      const rule = (r.data?.rules || []).find((x: any) => x.kind === 'price_deviation');
      const cur: string[] = rule?.params?.ignorePlatforms || [];
      await axios.patch(`${API}/pixa/${clientId}/anomalies/rules`, { kind: 'price_deviation', params: { ignorePlatforms: Array.from(new Set([...cur, ...platforms])) } });
      flash(t('Hariç tutuldu. Sonraki taramada bu kanallar için fiyat uyuşmazlığı üretilmez.'));
    } catch (e: any) { flash(e?.response?.data?.detail || t('Hata'), 'err'); }
  };

  const weeklyPlan = async () => {
    setPlan({ busy: true, text: null, err: null });
    try {
      const r = await axios.post(`${API}/pixa/${clientId}/ask`, {
        question: 'Açık bulgulara bakarak bu hafta yapmam gereken en önemli 3 işi sırala. Her madde için: hangi kanal, kaç ürün, neden önemli (satışa etkisi) ve tam olarak ne yapmalıyım. Kısa ve somut ol, genel tavsiye verme.',
        history: [],
      });
      setPlan({ busy: false, text: r.data?.answer || '—', err: null });
    } catch (e: any) { setPlan({ busy: false, text: null, err: e?.response?.data?.detail || t('Hata') }); }
  };

  if (loading && !data) return <Loader />;

  const rows: any[] = (data?.rows || []).filter((r: any) => !r.resolved_at);
  const counts = data?.counts || {};
  const scan_ = data?.last_scan;
  const pOf = (g: any) => resolvePlatform(g.scope, integrations);
  const insight = priceInsight(rows, details);

  // Kanal kartları: satış kanalı olan entegrasyonlar + her birinin açık bulguları
  const channels = integrations.filter((i: any) => i.isMarketplace !== false).map((i: any) => {
    const gs = rows.filter(g => pOf(g) === i.resourceName);
    const worst = gs.reduce((w: string, g: any) => ['high', 'medium', 'low', 'info'].indexOf(g.severity) < ['high', 'medium', 'low', 'info'].indexOf(w) ? g.severity : w, 'info');
    return { name: i.resourceName, active: !!i.active, groups: gs.length, records: gs.reduce((s: number, g: any) => s + (g.count || 0), 0), worst: gs.length ? worst : null };
  }).sort((a: any, b: any) => Number(b.active) - Number(a.active) || b.records - a.records);

  // Öncelik: önem → etkilenen kayıt
  const priority = rows.filter(g => g.severity === 'high' || g.severity === 'medium').slice(0, 3);
  const sections: [string, string, any[]][] = [
    ['high', 'Hemen İlgilenin', rows.filter(g => g.severity === 'high')],
    ['medium', 'Satış Kaybı Riski', rows.filter(g => g.severity === 'medium')],
    ['low', 'Gözden Geçirin', rows.filter(g => g.severity === 'low' || g.severity === 'info')],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Başlık: sağlık özeti */}
      <div style={{ background: `linear-gradient(135deg, ${tint(T.accent, 7)}, var(--py-surface) 55%)`, border: `1px solid ${T.border}`, borderRadius: 18,
        padding: isMobile ? 16 : '20px 24px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(260px,1fr) minmax(0,1.4fr)', gap: 18, alignItems: 'center' }}>
        <div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', color: T.amberDeepText,
            background: tint(T.accent, 16), borderRadius: 999, padding: '4px 10px', marginBottom: 10 }}>
            <Icon.sparkle size={12} sw={2} /> {upperUi(t('Mağaza Sağlığı'))}
          </span>
          <div style={{ fontSize: isMobile ? 18 : 21, fontWeight: 800, color: T.text, letterSpacing: '-0.02em', lineHeight: 1.25, marginBottom: 6 }}>
            {rows.length === 0 ? t('Her şey yolunda') : `${fmtN(rows.length)} ${t('açık bulgu')}, ${fmtN(counts.high || 0)} ${t('kritik')}`}
          </div>
          <div style={{ fontSize: 11.5, color: T.textDim, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' as const }}>
            {scan_ ? <span>{t('Son tarama')}: {timeAgo(scan_.finished_at || scan_.started_at)}{scan_.partial ? ` · ${t('kısmi')}` : ''}</span> : null}
            <Button variant="ghost" color={T.accent} disabled={scanning} onClick={scan} style={{ padding: '4px 10px', fontSize: 11 }}>{scanning ? t('Taranıyor…') : t('Şimdi Tara')}</Button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))', gap: 10 }}>
          {(['high', 'medium', 'low', 'info'] as const).map(s => (
            <div key={s} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: SEV_TEXT[s], marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: SEV_COLOR[s] }} />{t(SEV_LABEL[s])}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{fmtN(counts[s] || 0)}</div>
            </div>
          ))}
        </div>
      </div>
      <Toast msg={note.msg} tone={note.tone} />

      {/* Kanal durumu */}
      {channels.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.08em', marginBottom: 8 }}>{upperUi(t('Kanal Durumu'))}</div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 150 : 190}px, 1fr))`, gap: 8 }}>
            {channels.map((c: any) => (
              <div key={c.name} style={{ background: T.surface, border: `1px solid ${health === c.name ? T.accent : T.border}`, borderRadius: 12, padding: '10px 12px', opacity: c.active ? 1 : 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <MarketMark name={c.name} size={20} />
                  <b style={{ fontSize: 12.5, color: T.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</b>
                  {!c.active && <span style={{ fontSize: 10, color: T.textDim }}>{t('Pasif')}</span>}
                </div>
                <div style={{ fontSize: 11, color: c.worst ? SEV_TEXT[c.worst] : T.greenText, marginBottom: 8 }}>
                  {c.groups ? `${c.groups} ${t('açık bulgu')} · ${fmtN(c.records)} ${t('kayıt')}` : t('Sorun yok')}
                </div>
                <button onClick={() => setHealth(health === c.name ? null : c.name)} style={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: T.accent,
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{t('Bağlantıyı Test Et')} →</button>
              </div>
            ))}
          </div>
          {health && <div style={{ marginTop: 8 }}><HealthReport clientId={clientId} platform={health} onClose={() => setHealth(null)} /></div>}
        </div>
      )}

      {/* Öncelik + AI plan */}
      {rows.length > 0 && (
        <Card pad={0}>
          <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const, borderBottom: `1px solid ${T.border}` }}>
            <b style={{ fontSize: 13.5, color: T.text }}>{t('Öncelik Sırası')}</b>
            <Button variant="soft" color={T.accent} onClick={weeklyPlan} disabled={plan.busy} style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: 11.5 }}>
              <Icon.sparkle size={12} sw={2} /> {plan.busy ? t('AI düşünüyor…') : t('AI ile Haftalık Plan')}
            </Button>
          </div>
          <div style={{ padding: '6px 8px' }}>
            {priority.map((g, i) => {
              const p = pOf(g); const info = kindInfo(g.kind);
              return (
                <button key={g.id} onClick={() => { setOpen(s => new Set(s).add(g.id)); document.getElementById(`finding-${g.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  style={{ width: '100%', textAlign: 'left' as const, display: 'flex', gap: 12, alignItems: 'center', padding: '10px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 10 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 999, background: tint(SEV_COLOR[g.severity], 15), color: SEV_TEXT[g.severity], fontSize: 12, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: T.text }}>{t(info.title)}{p ? ` · ${p}` : ''} <span style={{ color: T.textDim, fontWeight: 600 }}>({fmtN(g.count)})</span></span>
                    <span style={{ display: 'block', fontSize: 11.5, color: T.textMid, marginTop: 2 }}>{info.todo(p, g, recordsOf(g))[0]}</span>
                  </span>
                  <span style={{ color: T.textDim }}>›</span>
                </button>
              );
            })}
          </div>
          {(plan.text || plan.err) && (
            <div style={{ margin: '0 16px 16px', padding: '12px 14px', borderRadius: 12, background: tint(T.accent, 7), border: `1px solid ${tint(T.accent, 35)}`,
              fontSize: 12.5, color: plan.err ? T.redText : T.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' as const }}>{plan.err || plan.text}</div>
          )}
        </Card>
      )}

      {/* Akıllı içgörü: Pixa ana fiyatı */}
      {insight && (
        <Card pad={18}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 32, height: 32, borderRadius: 10, background: tint(T.blue, 14), color: T.blueText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon.sparkle size={15} sw={2} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', color: T.blueText, marginBottom: 4 }}>{upperUi(t('Akıllı İçgörüler'))}</div>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                {fmtN(insight.products.length)} üründe sorun kanallarda değil, Pixa ana fiyatında görünüyor
              </div>
              <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.6, marginBottom: 10 }}>
                Bu ürünler {insight.platforms.join(', ')} kanallarının hepsinde aynı fiyattan satılıyor, ama Pixa'daki ana fiyat farklı.
                Örneğin <b style={{ color: T.text }}>{insight.products[0].sku}</b>: {Object.entries(insight.products[0].prices).map(([k, v]) => `${k} ${money(v)}`).join(' · ')} — Pixa {money(insight.products[0].pixa)}.
                Ya Pixa'daki ana fiyat eski, ya da kanallara bilinçli bir fiyat kuralı uygulanıyor.
                Eskiyse Pixa fiyatını kanal fiyatına çekin; bilinçliyse bu kanalları fiyat kuralından hariç tutun, gürültü kaybolur.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                <Button variant="soft" color={T.blue} onClick={() => excludeChannels(insight.platforms)} style={{ padding: '6px 12px', fontSize: 11.5 }}>{t('Bu kanalları fiyat kuralından hariç tut')}</Button>
                <Button variant="ghost" color={T.accent} onClick={() => exportCsv('pixa-ana-fiyat-farki.csv',
                  insight.products.map(p => ({ sku: p.sku, urun: p.name, pixa: p.pixa, ...Object.fromEntries(Object.entries(p.prices).map(([k, v]) => [k, v])) })),
                  [{ key: 'sku', label: 'SKU' }, { key: 'urun', label: 'Ürün' }, { key: 'pixa', label: 'Pixa Fiyatı' }, ...insight.platforms.map(k => ({ key: k, label: k }))])}
                  style={{ padding: '6px 12px', fontSize: 11.5 }}>{t('CSV İndir')} ({fmtN(insight.products.length)})</Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {rows.length === 0 && (
        <Card pad={40}><div style={{ textAlign: 'center' as const }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.greenText, marginBottom: 6 }}>{t('Her şey yolunda')}</div>
          <div style={{ fontSize: 12.5, color: T.textDim }}>{t('Açık bulgu yok.')}</div>
        </div></Card>
      )}

      {sections.map(([sev, label, gs]) => gs.length > 0 && (
        <div key={sev} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: SEV_TEXT[sev], letterSpacing: '0.08em', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: SEV_COLOR[sev] }} />
            {upperUi(t(label))} · {gs.length}
          </div>
          {gs.map(g => (
            <FindingCard key={g.id} g={g} platform={pOf(g)} open={open.has(g.id)} onToggle={() => toggle(g.id)} records={recordsOf(g)}
              clientId={clientId} onAction={a => runAction(g, a, pOf(g))} onAck={() => ack(g)} />
          ))}
        </div>
      ))}

      <ConfirmDangerModal open={!!danger} busy={busy} error={err}
        title={danger ? `${danger.action.label}` : ''}
        description={danger?.action.id === 'send'
          ? `${fmtN(new Set(recordsOf(danger.group).map((s: any) => s.productId).filter(Boolean)).size)} ürün ${dative(danger.platform)} gönderilecek. Değişiklik pazaryerindeki yayına yansır.`
          : t('Pazaryeri görevi yeniden çalıştırılacak. İşlem dış sisteme yazar.')}
        items={danger ? recordsOf(danger.group).slice(0, 10).map((s: any) => [s.sku, s.name].filter(Boolean).join(' · ')) : []}
        onCancel={() => setDanger(null)} onConfirm={confirmAction} />
    </div>
  );
}

function FindingCard({ g, platform, open, onToggle, records, clientId, onAction, onAck }: {
  g: any; platform: string; open: boolean; onToggle: () => void; records: Rec[]; clientId: number;
  onAction: (a: Action) => void; onAck: () => void;
}) {
  const { t } = useLang();
  const info = kindInfo(g.kind);
  const acts = actionsFor(g, platform);
  const [ai, setAi] = useState<{ busy: boolean; text: string | null; err: string | null }>({ busy: false, text: null, err: null });
  const color = SEV_COLOR[g.severity] || T.textDim;
  const isChannel = !!platform && !['low_stock', 'pending_questions', 'problematic_shipment'].includes(g.kind);

  const askAi = async () => {
    setAi({ busy: true, text: null, err: null });
    const sample = records.slice(0, 15).map(r => [r.sku, r.name, r.value, r.effectivePrice && `pazaryeri ${r.effectivePrice}`, r.pixaPrice && `pixa ${r.pixaPrice}`, r.pixaStock !== undefined && `stok ${r.pixaStock}`].filter(Boolean).join(' | '));
    try {
      const r = await axios.post(`${API}/pixa/${clientId}/ask`, {
        question: `Şu bulguyu nasıl çözerim? Bulgu: ${info.title}${platform ? ` (${platform})` : ''}, ${g.count} kayıt. Detay: ${g.detail}. Örnek kayıtlar:\n${sample.join('\n')}\n` +
          'Bana kısa, adım adım ve bu kayıtlara özel (SKU vererek) bir çözüm yolu yaz. Hangi ürünlere öncelik vermem gerektiğini de söyle.',
        history: [],
      });
      setAi({ busy: false, text: r.data?.answer || '—', err: null });
    } catch (e: any) { setAi({ busy: false, text: null, err: e?.response?.data?.detail || t('Hata') }); }
  };

  return (
    <div id={`finding-${g.id}`} style={{ background: T.surface, border: `1px solid ${open ? tint(color, 45) : T.border}`, borderRadius: 14, overflow: 'hidden',
      boxShadow: open ? `0 6px 20px ${tint(color, 92)}` : 'none', opacity: g.ack_at && !open ? 0.75 : 1 }}>
      <button onClick={onToggle} style={{ width: '100%', textAlign: 'left' as const, display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px',
        border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
        <span style={{ width: 4, alignSelf: 'stretch', borderRadius: 3, background: color, flexShrink: 0 }} />
        {isChannel ? <MarketMark name={platform} size={28} /> : (
          <span style={{ width: 28, height: 28, borderRadius: 8, background: tint(color, 14), color: SEV_TEXT[g.severity], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon.warningTriangle size={14} sw={2} />
          </span>
        )}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
            <b style={{ fontSize: 13.5, color: T.text }}>{t(info.title)}</b>
            {isChannel && <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid }}>{platform}</span>}
            {g.ack_at && <span style={{ fontSize: 10, color: T.textDim, background: 'var(--py-subtle-bg)', borderRadius: 999, padding: '1px 7px' }}>{t('görüldü')}</span>}
          </span>
          <span style={{ display: 'block', fontSize: 11.5, color: T.textMid, marginTop: 3, lineHeight: 1.5,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: open ? 'normal' as const : 'nowrap' as const }}>{info.meaning(platform, g)}</span>
        </span>
        <span style={{ textAlign: 'right' as const, flexShrink: 0 }}>
          <b style={{ display: 'block', fontSize: 17, color: T.text }}>{fmtN(g.count)}</b>
          <span style={{ fontSize: 10, color: T.textDim }}>{t('kayıt')}</span>
        </span>
        <span style={{ color: T.textDim, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', display: 'flex' }}><Icon.chevron size={16} /></span>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'var(--py-subtle-bg)', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: '0.06em', marginBottom: 6 }}>{upperUi(t('Ne yapmalısınız?'))}</div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: T.text, lineHeight: 1.7 }}>
              {info.todo(platform, g, records).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, alignItems: 'center' }}>
            {acts.map(a => (
              <Button key={a.id} variant={a.primary ? 'solid' : 'ghost'} color={a.push ? T.red : T.accent} onClick={() => onAction(a)}
                style={{ padding: '7px 14px', fontSize: 12, ...(a.primary ? { color: '#fff' } : {}) }}>{t(a.label)}</Button>
            ))}
            <Button variant="soft" color={T.accent} onClick={askAi} disabled={ai.busy} style={{ padding: '7px 12px', fontSize: 12 }}>
              <Icon.sparkle size={12} sw={2} /> {ai.busy ? t('AI düşünüyor…') : t('AI Çözüm Önerisi')}
            </Button>
            {!g.ack_at && <Button variant="ghost" color={T.textDim} onClick={onAck} style={{ padding: '7px 12px', fontSize: 12, marginLeft: 'auto' }}>{t('Gördüm')}</Button>}
          </div>

          {(ai.text || ai.err) && (
            <div style={{ padding: '12px 14px', borderRadius: 12, background: tint(T.accent, 7), border: `1px solid ${tint(T.accent, 35)}`,
              fontSize: 12.5, color: ai.err ? T.redText : T.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' as const }}>{ai.err || ai.text}</div>
          )}

          <FindingRecords kind={g.kind} records={records} platform={platform} />
          <div style={{ fontSize: 10.5, color: T.textFaint }}>{t('İlk görülme')}: {timeAgo(g.first_seen_at)} · {t('Son görülme')}: {timeAgo(g.last_seen_at)}</div>
        </div>
      )}
    </div>
  );
}
